import { Alert, Button, Card, Container, Group, PasswordInput, SimpleGrid, TextInput, Title } from "@mantine/core";
import { useState, useContext, useEffect } from "react";
import { UserContext } from "../App";
import { IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/Navigation/navBar";

function LoginProfilePage() {
  const [loginCredentials, setLoginCredentials] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  
  const {
    authenticatedUser,
    setAuthenticatedUser,
  }: { authenticatedUser: IUserSessionInfo | null; setAuthenticatedUser: any } = useContext(UserContext);
  
  // Initial state for user profile
  const [userProfile, setUserProfile] = useState<IUpdateProfile>({
    email: authenticatedUser?.email || "",
    first_name: authenticatedUser?.first_name || "",
    last_name: authenticatedUser?.last_name || "",
    current_password: "",
    new_password: "",
  });
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updateStatusMessage, setUpdateStatusMessage] = useState({ status: 0, message: "" });
  const [loginErrorMessage, setLoginErrorMessage] = useState("");

  useEffect(() => {
    // Update the userProfile state whenever authenticatedUser changes
    if (authenticatedUser) {
      setUserProfile((prevProfile) => ({
        ...prevProfile,
        first_name: authenticatedUser.first_name,
        last_name: authenticatedUser.last_name,
        email: authenticatedUser.email,
      }));
    }
  }, [authenticatedUser]);

  const handleLogin = async () => {
    try {
      const fetchRes = await fetch("/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginCredentials),
        credentials: "include",
      });
      const response = await fetchRes.json();
      if (response.message === "Success" && response.user._id) {
        setAuthenticatedUser(response.user);
        if (response.user.scope === "SuperAdmin" || response.user.scope === "Admin") {
          navigate("/");
        } else if (response.user.scope === "User") {
          navigate("/");
        }
      } else {
        setLoginErrorMessage(response.message);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleLogout = async () => {
    try {
      const fetchRes = await fetch("/auth/logout", {
        credentials: "include",
      });
      await fetchRes.json();
      setAuthenticatedUser({
        _id: null,
        email: "",
        first_name: "",
        last_name: "",
        scope: "",
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleUpdate = async () => {
    if (newPassword !== confirmPassword) {
      setUpdateStatusMessage({ status: 403, message: "Passwords do not match" });
      return;
    }

    const userProfileLocalCopy: IUpdateProfile = { ...userProfile, email: authenticatedUser?.email || "" };
    userProfileLocalCopy.new_password = newPassword || userProfileLocalCopy.current_password;

    try {
      const fetchRes = await fetch("/auth/update", {
        credentials: "include",
        method: "POST",
        body: JSON.stringify(userProfileLocalCopy),
        headers: { "Content-Type": "application/json" },
      });
      const response = await fetchRes.json();
      setUpdateStatusMessage(response);
      if (response.status === 200) {
        const userResponse = await handleGetUser();
        setAuthenticatedUser(userResponse.user);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleGetUser = async () => {
    try {
      const fetchRes = await fetch("/auth/user", { method: "POST", credentials: "include" });
      return await fetchRes.json();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <NavBar />
      <Container style={{ marginTop: "80px" }}>
        {authenticatedUser?._id ? (
          <>
            <Title>Hi {authenticatedUser.first_name},</Title>
            <Card>
              <TextInput placeholder="Email address" label="Email address" value={authenticatedUser.email} disabled />
              <SimpleGrid cols={1} mt="md" breakpoints={[{ minWidth: "sm", cols: 2 }]}>
                <TextInput
                  placeholder="Enter first name"
                  label="First name"
                  value={userProfile.first_name}
                  onChange={(event) =>
                    setUserProfile((userProfile) => ({ ...userProfile, first_name: event.target.value }))
                  }
                />
                <TextInput
                  placeholder="Enter last name"
                  label="Last name"
                  value={userProfile.last_name}
                  onChange={(event) =>
                    setUserProfile((userProfile) => ({ ...userProfile, last_name: event.target.value }))
                  }
                />
              </SimpleGrid>
              <TextInput
                mt="md"
                placeholder="Enter current password"
                label="Current password"
                onChange={(event) =>
                  setUserProfile((userProfile) => ({ ...userProfile, current_password: event.target.value }))
                }
              />
              <SimpleGrid cols={1} mt="md" breakpoints={[{ minWidth: "sm", cols: 2 }]}>
                <PasswordInput
                  placeholder="Enter new password"
                  label="New password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                <PasswordInput
                  placeholder="Re-enter new password"
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  error={confirmPassword !== newPassword}
                />
              </SimpleGrid>
              <Group mt="md" position="apart">
                <Button onClick={handleUpdate} variant="outline">
                  Update
                </Button>
                <Button onClick={handleLogout} variant="subtle">
                  Log out
                </Button>
              </Group>
              {updateStatusMessage.status !== 0 && (
                <Card.Section mt="md">
                  <Alert
                    icon={<IconAlertCircle />}
                    color={updateStatusMessage.status === 200 ? "green" : "red"}
                    title={updateStatusMessage.status === 200 ? "Success" : "Error"}
                  >
                    {updateStatusMessage.message}
                  </Alert>
                </Card.Section>
              )}
            </Card>
          </>
        ) : (
          <>
            <Title>Login</Title>
            <form>
              <Card>
                <TextInput
                  placeholder="Enter email address"
                  label="Email"
                  value={loginCredentials.email}
                  onChange={(event) =>
                    setLoginCredentials({ ...loginCredentials, email: event.target.value })
                  }
                />
                <PasswordInput
                  placeholder="Password"
                  label="Password"
                  value={loginCredentials.password}
                  onChange={(event) =>
                    setLoginCredentials({ ...loginCredentials, password: event.target.value })
                  }
                />
                <Button mt="md" onClick={handleLogin}>
                  Login
                </Button>
                {loginErrorMessage && (
                  <Card.Section mt="md">
                    <Alert icon={<IconAlertCircle />} color="red" title="Error">
                      {loginErrorMessage}
                    </Alert>
                  </Card.Section>
                )}
              </Card>
            </form>
          </>
        )}
      </Container>
    </>
  );
}

export default LoginProfilePage;